import {Component, PanelLifecycleContext} from 'panel';
import produce from 'immer';

import {Bucket} from 'common/widgets/bucket-pane/types';
import {QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {ConversionWindow, CountType} from 'common/report/funnels/models/types';

import {BookmarkType} from './report/queries/app-api/bookmark-client';

export type AdditionalContext = ViewAsFlowsContext | EmptyObj;

export type QueryContext = {
  bookmarkType: BookmarkType;
  dataGroupId?: string;
  dateRange?: Partial<DateRange>;
  events?: QueryEventEntry[];
  comparedDateRange?: ComparisonDate;
  originDashboard?: DashboardOrigin;
  segments: Segment[];
  filters: PropertyFilter[];
  conversionCriteria?: ConversionCriteria;
  querySamplingEnabled?: boolean;
} & Partial<AdditionalContext>;

export type SegmentationMetadata = {
  segment: Segment;
  numericBucket?: Bucket;
};

export type ConversionCriteria = {
  conversionWindow: ConversionWindow;
  countType: CountType;
  exclusionSteps: QueryEventEntry[];
  holdingPropertyConstant: Segmentation[];
};

export type ViewAsFlowsContext = {
  viewAsFlowsContext: {funnelStep: number};
};

export function isViewAsFlowsContext(additionalContext: AdditionalContext): additionalContext is ViewAsFlowsContext {
  return !!additionalContext?.viewAsFlowsContext;
}

export interface QueryContextSupport {
  updateLastReportQueryContext(queryContext: QueryContext);
  getReportQueryContext(): QueryContext;
}

export class QueryContextManager implements PanelLifecycleContext {
  private connectedComponents: Set<Component<any>> = new Set();
  public isDirty = false;
  public query: QueryContext = {
    bookmarkType: null,
    dataGroupId: null,
    dateRange: new FullLengthDateRange().getAttributes(),
    events: [],
    comparedDateRange: null,
    originDashboard: null,
    segments: [],
    filters: [],
    conversionCriteria: null,
    querySamplingEnabled: false,
  };
  public segmentationMetadatas: SegmentationMetadata[] = [];

  public updateQuery(query: Partial<QueryContext>, preventUpdate = false) {
    this.isDirty = true;
    this.query = produce(this.query, (draft) => Object.assign(draft, query));

    if (preventUpdate === false) {
      this.updateComponents();
    }
  }

  public updateSegmentationMetadatas(segmentationMetadatas: SegmentationMetadata[]) {
    this.segmentationMetadatas = segmentationMetadatas;
    this.updateComponents();
  }

  public get(field: keyof QueryContext) {
    return this.query[field];
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
  }
}
